import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, Cloud } from "lucide-react";

/**
 * Searchable input backed by Salesforce.
 * - `endpoint`: backend path, e.g. "/salesforce/accounts/search"
 * - `value` / `onChange(text)`: free-text fallback (user can still type a custom value)
 * - `onPick(item)`: called when a suggestion is chosen
 * - `renderItem(item)`: optional custom row renderer; defaults to item.name
 */
export default function SalesforceAutocomplete({
  endpoint,
  value,
  onChange,
  onPick,
  placeholder = "Search Salesforce…",
  testId = "sf-autocomplete",
  renderItem,
  className = "",
  extraParams = null,
  minChars = 2,
  disabled = false,
  disabledHint = "",
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const paramsKey = JSON.stringify(extraParams || {});

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const runSearch = async (q) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(endpoint, { params: { q, ...(extraParams || {}) } });
      setResults(data || []);
      setOpen(true);
    } catch (e) {
      setResults([]);
      setError(e.response?.data?.detail || "Salesforce unavailable");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disabled) { setResults([]); setOpen(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (value || "").trim();
    const hasExtras = !!(extraParams && Object.values(extraParams).some((v) => v));
    if (q.length < minChars && !hasExtras) { setResults([]); setError(""); return; }
    debounceRef.current = setTimeout(() => runSearch(q), 250);
    return () => clearTimeout(debounceRef.current);
  }, [value, endpoint, paramsKey, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputCls = `w-full border border-zinc-300 bg-white px-3 py-2 pr-9 text-sm rounded-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none ${disabled ? "bg-zinc-50 text-zinc-400 cursor-not-allowed" : ""}`;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        data-testid={testId}
        className={inputCls}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          if (disabled) return;
          const hasExtras = !!(extraParams && Object.values(extraParams).some((v) => v));
          if (results.length) setOpen(true);
          else if (hasExtras && !loading) runSearch((value || "").trim());
        }}
        placeholder={disabled ? (disabledHint || placeholder) : placeholder}
        autoComplete="off"
        disabled={disabled}
      />
      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Cloud size={14} strokeWidth={1.5} />}
      </div>

      {open && !disabled && (results.length > 0 || error) && (
        <div data-testid={`${testId}-dropdown`} className="absolute z-50 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-64 overflow-y-auto">
          {error ? (
            <p className="text-xs text-red-600 px-3 py-2">{error}</p>
          ) : (
            results.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`${testId}-option-${item.id}`}
                onClick={() => { onPick?.(item); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-blue-50/60 border-b border-zinc-100 last:border-b-0"
              >
                {renderItem ? renderItem(item) : (
                  <p className="text-sm">{item.name}</p>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
